import { PrinterIcon } from '@phosphor-icons/react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { useSetting } from '@/features/settings';
import { flights, logbookEntries, orderEntriesChronologically, settings } from '@/lib/db';
import { formatLocalDate } from '@/lib/format';

import { confirmedOnly, sumTotals } from './aggregate';
import { CAA_1373_COLUMNS, CAA_1373_GROUPS, CAA_1373_SUBGROUPS } from './caa-columns';
import { columnHeaderName } from './columns';
import { computeRecentExperience, evaluateExpiryReminders, RECENCY_WINDOW_DAYS } from './currency';
import { PendingDrafts } from './PendingDrafts';
import { LOGBOOK_OPENING_BALANCE_KEY } from './settings-key';
import { formatDecimalHours, roundDecimalHours } from './time';
import { computeRunningTotals } from './totals';
import { useCurrencyReminders } from './use-currency-reminders';

/**
 * The CAA-1373-shaped printable page — `NZ-LOGBOOK.md` §2.2: "printing is a
 * first-class feature, not an export afterthought." This is a page laid out
 * *like* a CAA 1373 page (the same column groups, the same brought-forward /
 * this-page / total-to-date arithmetic strip at the foot of each page), not a
 * pixel-exact reproduction of the government form — the real form isn't
 * bundled with this repository to typeset against, and a plausible fake would
 * be worse than an honestly different layout that still transcribes fast
 * (`docs/PHASE-6.md`).
 *
 * Only confirmed entries print. `NZ-LOGBOOK.md` §2.1's compliance line — "this
 * app mirrors [the bound logbook] and does the maths" — only holds for
 * numbers the pilot has actually looked at; an unapproved draft has no
 * business on a page meant to be transcribed into the legal record.
 *
 * Uses `window.print()` rather than a bundled PDF library: every browser's
 * "Save as PDF" print destination already produces one, and the CAA-shaped
 * layout is exactly as good on paper as it is in that PDF — no dependency
 * earns its weight duplicating what the browser does for free.
 */

const ROWS_PER_PAGE = 20;

/** Where TOTAL FLIGHT TIME sits, so the footer's carried-forward figures land under it. */
const totalColumnIndex = CAA_1373_COLUMNS.findIndex((column) => column.id === 'time.totalHr');

function chunk<T>(items: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

export function PrintView() {
  const entries = useLiveQuery(() => logbookEntries.listOrdered(), [], null);
  const openingBalance = useLiveQuery(() => settings.get(LOGBOOK_OPENING_BALANCE_KEY, 0), [], null);
  const allFlights = useLiveQuery(() => flights.list(), [], null);
  const reminders = useCurrencyReminders();
  const [timeZone] = useSetting('timeZone');

  const flightById = useMemo(
    () => new Map((allFlights ?? []).map((flight) => [flight.id, flight])),
    [allFlights],
  );

  /*
   * The same order `computeRunningTotals` walks in, so the "to date" cell
   * beside a row is that row's running total and the arithmetic strip at the
   * foot of a page is the arithmetic of *that* page.
   */
  const confirmed = useMemo(
    () => (entries === null ? [] : orderEntriesChronologically(confirmedOnly(entries), flightById)),
    [entries, flightById],
  );
  const totals = useMemo(
    () => computeRunningTotals(confirmed, openingBalance ?? 0, flightById),
    [confirmed, openingBalance, flightById],
  );
  const totalById = useMemo(() => new Map(totals.map((t) => [t.entryId, t])), [totals]);
  const asOf = formatLocalDate(new Date().toISOString(), { format: 'iso', timeZone });
  const recentExperience = useMemo(
    () => computeRecentExperience(entries ?? [], asOf),
    [entries, asOf],
  );
  const reminderStatuses = useMemo(
    () => (reminders === null ? [] : evaluateExpiryReminders(reminders, asOf)),
    [reminders, asOf],
  );
  const grandTotals = useMemo(() => sumTotals(confirmed), [confirmed]);

  const pages = useMemo(() => chunk(confirmed, ROWS_PER_PAGE), [confirmed]);

  if (entries === null || openingBalance === null || reminders === null) {
    return (
      <p className="text-muted-foreground p-6 text-sm print:hidden">Opening the local database…</p>
    );
  }

  return (
    <div className="text-foreground bg-background">
      <div className="flex items-center justify-between gap-4 p-4 print:hidden">
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm">
            {confirmed.length} approved {confirmed.length === 1 ? 'entry' : 'entries'}
            {confirmed.length === 0
              ? '.'
              : ` · ${String(pages.length)} ${pages.length === 1 ? 'page' : 'pages'}.`}
          </p>
          <PendingDrafts />
        </div>
        <Button
          disabled={confirmed.length === 0}
          onClick={() => {
            window.print();
          }}
        >
          <PrinterIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
          Print / save as PDF
        </Button>
      </div>

      <section className="break-after-page p-8 text-sm">
        <h1 className="text-lg font-semibold tracking-tight">Logbook summary</h1>
        <p className="text-muted-foreground mt-1">As of {asOf}.</p>

        <table className="mt-4 w-full max-w-md border-collapse text-sm">
          <tbody>
            <tr className="border-border border-b">
              <td className="py-1">Brought forward</td>
              <td className="tabular py-1 text-right">{formatDecimalHours(openingBalance)}</td>
            </tr>
            <tr className="border-border border-b">
              <td className="py-1">Total time (this logbook)</td>
              <td className="tabular py-1 text-right">{formatDecimalHours(grandTotals.totalHr)}</td>
            </tr>
            <tr className="border-border border-b font-semibold">
              <td className="py-1">Total to date</td>
              <td className="tabular py-1 text-right">
                {formatDecimalHours(openingBalance + grandTotals.totalHr)}
              </td>
            </tr>
            <tr className="border-border border-b">
              <td className="py-1">PIC</td>
              <td className="tabular py-1 text-right">{formatDecimalHours(grandTotals.picHr)}</td>
            </tr>
            <tr className="border-border border-b">
              <td className="py-1">Dual</td>
              <td className="tabular py-1 text-right">{formatDecimalHours(grandTotals.dualHr)}</td>
            </tr>
            <tr className="border-border border-b">
              <td className="py-1">Night</td>
              <td className="tabular py-1 text-right">{formatDecimalHours(grandTotals.nightHr)}</td>
            </tr>
            <tr className="border-border border-b">
              <td className="py-1">Instrument (actual + simulated)</td>
              <td className="tabular py-1 text-right">
                {formatDecimalHours(
                  grandTotals.actualInstrumentHr + grandTotals.simulatedInstrumentHr,
                )}
              </td>
            </tr>
            <tr className="border-border border-b">
              <td className="py-1">Takeoffs (day / night)</td>
              <td className="tabular py-1 text-right">
                {grandTotals.takeoffsDay} / {grandTotals.takeoffsNight}
              </td>
            </tr>
            <tr>
              <td className="py-1">Landings (day / night)</td>
              <td className="tabular py-1 text-right">
                {grandTotals.landingsDay} / {grandTotals.landingsNight}
              </td>
            </tr>
          </tbody>
        </table>

        <h2 className="mt-6 text-sm font-semibold tracking-tight">Currency, as of {asOf}</h2>
        <table className="mt-2 w-full max-w-2xl border-collapse text-sm">
          <tbody>
            {recentExperience.byType.flatMap((type) =>
              type.statuses.map((status) => (
                <tr key={`${type.aircraftType} ${status.id}`} className="border-border border-b">
                  <td className="py-1">
                    {type.aircraftType} — {status.label}
                  </td>
                  <td className="py-1">
                    {status.level === 'ok' ? 'Current' : 'Not current'} ({status.count}/
                    {status.required} in {status.windowDays} days
                    {status.lapsesOn === undefined ? '' : `, until ${status.lapsesOn}`})
                  </td>
                  <td className="text-muted-foreground py-1 text-xs">{status.ruleReference}</td>
                </tr>
              )),
            )}
            {recentExperience.untyped.entryCount === 0 ? null : (
              <tr className="border-border border-b">
                <td className="py-1">Entries with no aircraft type</td>
                <td className="py-1">
                  {recentExperience.untyped.entryCount} in the last {RECENCY_WINDOW_DAYS} days, not
                  counted
                </td>
                <td className="text-muted-foreground py-1 text-xs" />
              </tr>
            )}
            {reminderStatuses.map(({ reminder, level, daysRemaining }) => (
              <tr key={reminder.id} className="border-border border-b">
                <td className="py-1">{reminder.label}</td>
                <td className="py-1 whitespace-nowrap">
                  {level === 'unknown'
                    ? 'No date set'
                    : reminder.expiryDate === null
                      ? '—'
                      : `${level === 'expired' ? 'Expired' : level === 'expiring' ? 'Expiring' : 'Current'} — ${reminder.expiryDate} (${String(daysRemaining)} days)`}
                </td>
                <td className="text-muted-foreground py-1 text-xs">{reminder.ruleReference}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Paper only: on screen the logbook layout already says it, once. */}
        <p className="text-muted-foreground mt-6 hidden text-xs print:block">
          The bound logbook remains the legal record.
        </p>
      </section>

      {pages.map((pageEntries, pageIndex) => {
        /*
         * Brought forward is what stood *before* this page: the opening
         * balance on page one, and the previous page's last "to date"
         * afterwards. It used to be read off this page's first row instead,
         * as that row's total minus its own hours — which is only the same
         * number when the first row printed is also the first row counted.
         * With a day's flights ordered one way on the page and another in the
         * running total, page one opened with hours it had not been brought
         * any of, and a pilot with an empty opening balance read a
         * brought-forward figure out of thin air.
         */
        const previousEntry = confirmed[pageIndex * ROWS_PER_PAGE - 1];
        const broughtForward =
          previousEntry === undefined
            ? openingBalance
            : (totalById.get(previousEntry.id)?.total ?? openingBalance);
        const pageTotal = sumTotals(pageEntries).totalHr;
        const toDate = roundDecimalHours(broughtForward + pageTotal);

        return (
          <section
            key={pageIndex}
            className={`p-8 text-xs ${pageIndex < pages.length - 1 ? 'break-after-page' : ''}`}
          >
            <div className="mb-2 flex items-baseline justify-between">
              <h2 className="text-sm font-semibold tracking-tight">Flight log</h2>
              <span className="text-muted-foreground">
                Page {pageIndex + 1} of {pages.length}
              </span>
            </div>
            <table className="w-full border-collapse text-[10px]">
              <thead>
                <tr className="border-border border-b">
                  {CAA_1373_GROUPS.map((group, index) => (
                    <th
                      key={`${group.label}-${String(index)}`}
                      colSpan={group.span}
                      className="border-border border-l px-1 py-0.5 text-left font-medium uppercase first:border-l-0"
                    >
                      {group.label}
                    </th>
                  ))}
                  <th className="border-border border-l px-1 py-0.5 text-right font-medium">
                    To date
                  </th>
                </tr>
                {/* 1373's DAY/NIGHT band, inside the two engine groups only. */}
                <tr className="border-border border-b">
                  {CAA_1373_SUBGROUPS.map((run, index) =>
                    run.label === '' ? (
                      <th key={`blank-${String(index)}`} colSpan={run.span} />
                    ) : (
                      <th
                        key={`${run.label}-${String(index)}`}
                        colSpan={run.span}
                        className="border-border border-l px-1 py-0.5 text-left font-medium uppercase"
                      >
                        {run.label}
                      </th>
                    ),
                  )}
                  <th />
                </tr>
                <tr className="border-border text-muted-foreground border-b">
                  {CAA_1373_COLUMNS.map((column) => {
                    const name = columnHeaderName(CAA_1373_COLUMNS, column);
                    return (
                      <th
                        key={column.id}
                        {...(name === column.label ? {} : { 'aria-label': name })}
                        className={`px-1 py-0.5 font-normal ${column.align === 'right' ? 'text-right' : 'text-left'}`}
                      >
                        {column.label}
                      </th>
                    );
                  })}
                  <th />
                </tr>
              </thead>
              <tbody>
                {pageEntries.map((entry) => (
                  <tr key={entry.id} className="border-border border-b">
                    {CAA_1373_COLUMNS.map((column) => (
                      <td
                        key={column.id}
                        className={`tabular px-1 py-0.5 ${column.align === 'right' ? 'text-right' : ''}`}
                      >
                        {column.read(entry)}
                      </td>
                    ))}
                    <td className="tabular px-1 py-0.5 text-right">
                      {formatDecimalHours(totals.find((t) => t.entryId === entry.id)?.total ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/*
               * The arithmetic strip a 1373 page carries at its foot, and the
               * reason §22 of the redesign brief asks for a total on every
               * column rather than only on Total Flight Time: a page whose
               * instrument hours and landings do not carry forward is a page
               * that has to be added up again by hand.
               *
               * Brought forward and total to date stay on Total Flight Time
               * alone, because the opening balance the pilot enters is one
               * number — an hours figure carried in from the bound logbook —
               * and inventing a brought-forward count for each of the other
               * columns would be this app making up the past.
               */}
              <tfoot>
                <tr className="border-border tabular border-t-2 font-semibold">
                  {CAA_1373_COLUMNS.map((column, index) => (
                    <td
                      key={column.id}
                      data-testid={index === totalColumnIndex ? 'footer-this-page' : undefined}
                      className={`px-1 py-1 ${column.align === 'right' ? 'text-right' : ''}`}
                    >
                      {index === 0 ? 'This page' : (column.total?.(pageEntries) ?? '')}
                    </td>
                  ))}
                  <td />
                </tr>
                <tr className="border-border tabular border-t font-semibold">
                  <td colSpan={totalColumnIndex} className="px-1 py-1 text-right">
                    Brought forward
                  </td>
                  <td data-testid="footer-brought-forward" className="px-1 py-1 text-right">
                    {formatDecimalHours(broughtForward)}
                  </td>
                  <td colSpan={CAA_1373_COLUMNS.length - totalColumnIndex} />
                </tr>
                <tr className="border-border tabular border-t font-semibold">
                  <td colSpan={totalColumnIndex} className="px-1 py-1 text-right">
                    Total to date
                  </td>
                  <td data-testid="footer-to-date" className="px-1 py-1 text-right">
                    {formatDecimalHours(toDate)}
                  </td>
                  <td colSpan={CAA_1373_COLUMNS.length - totalColumnIndex} />
                </tr>
              </tfoot>
            </table>
          </section>
        );
      })}

      {pages.length === 0 ? (
        <p className="text-muted-foreground p-8 text-sm">
          No approved entries. Approve a logbook entry to print.
        </p>
      ) : null}
    </div>
  );
}
